// The prompt picker — tabs over a copy box.
//
// One prompt assumed everyone arrives with the same intent. They don't: some
// are starting from nothing, some have an app already, some are still deciding
// whether the platform is even right for them. Each gets its own prompt; the
// box stays the single interaction.

import {
	Box,
	Button,
	CopyButton,
	Group,
	Paper,
	SegmentedControl,
	Stack,
	Text,
} from "@mantine/core";
import { IconCheck, IconCopy, IconRobot } from "@tabler/icons-react";
import { useState } from "react";
import { useSession } from "../session/SessionProvider.tsx";
import type { AgentPrompt } from "../lib/agent-prompts.ts";

export function PromptPicker({
	prompts,
	maxHeight = 240,
}: {
	prompts: AgentPrompt[];
	maxHeight?: number;
}) {
	const [id, setId] = useState(prompts[0]?.id ?? "");
	const { client } = useSession();
	const origin = typeof window === "undefined" ? "https://s3p.dev" : window.location.origin;
	const active = prompts.find((p) => p.id === id) ?? (prompts[0] as AgentPrompt);
	const text = active.build(origin);

	return (
		<Stack gap="sm" w="100%" align="center">
			<SegmentedControl
				value={id}
				onChange={setId}
				size="sm"
				radius="md"
				data={prompts.map((p) => ({ value: p.id, label: p.label }))}
				styles={{ root: { flexWrap: "wrap" } }}
			/>

			<Paper
				withBorder
				radius="lg"
				w="100%"
				maw={720}
				style={{ overflow: "hidden", boxShadow: "0 20px 60px -30px rgba(0,0,0,0.6)" }}
			>
				{/* Terminal chrome: this is a command you hand to a machine. */}
				<Group
					gap={8}
					px="md"
					py={8}
					c="dimmed"
					style={{
						borderBottom: "1px solid var(--mantine-color-default-border)",
						background: "var(--mantine-color-default-hover)",
					}}
				>
					<IconRobot size={15} />
					<Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: "0.08em" }}>
						Paste this to your coding agent
					</Text>
				</Group>
				<Box p="md">
				<Box
					component="pre"
					m={0}
					style={{
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
						fontFamily: "var(--mantine-font-family-monospace)",
						fontSize: 12.5,
						lineHeight: 1.6,
						maxHeight,
						overflowY: "auto",
						color: "var(--mantine-color-dimmed)",
					}}
				>
					{text}
				</Box>
				</Box>
			</Paper>

			<Text size="sm" c="dimmed" ta="center" maw={640}>
				{active.hint}
			</Text>

			<CopyButton value={text}>
				{({ copied, copy }) => (
					<Button
						size="md"
						color={copied ? "teal" : undefined}
						leftSection={copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
						onClick={() => {
							copy();
							// The single most important signal on the page: which prompt did
							// they take? Fire-and-forget; never blocks the copy.
							void client?.telemetry.track("prompt_copied", { prompt: active.id });
						}}
					>
						{copied ? "Copied — now paste it" : "Copy prompt for your agent"}
					</Button>
				)}
			</CopyButton>
		</Stack>
	);
}
